import React, { useEffect, useState } from 'react';
import axios from 'axios';
import icon from '../assets/icon.png';
import { useAppSetting } from '../context/AppSettingContext';
import { Users, FileText, MessageSquare, TrendingUp, BarChart2, Activity, RefreshCw, ArrowUpRight } from 'lucide-react';

/* ─── Styles ────────────────────────────────────────────────────────────────── */
const S = {
  page: { padding: '0', fontFamily: "'Inter', 'Segoe UI', sans-serif" },
  header: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 28 },
  title: { fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.3px' },
  subtitle: { fontSize: '0.85rem', color: '#64748b', margin: '4px 0 0' },
  refreshBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px',
    background: '#1e293b', color: '#fff', border: 'none', borderRadius: 10,
    fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s'
  },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 },
  kpiCard: (gradient) => ({
    background: gradient, borderRadius: 16, padding: '20px 22px',
    color: '#fff', display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', minHeight: 120, boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
  }),
  kpiLabel: { fontSize: '0.75rem', fontWeight: 600, opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.5px' },
  kpiValue: { fontSize: '2.1rem', fontWeight: 800, lineHeight: 1, margin: '6px 0 4px' },
  kpiSub: { fontSize: '0.75rem', opacity: 0.75 },
  kpiIcon: {
    background: 'rgba(255,255,255,0.22)', borderRadius: 12,
    width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  chartsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 },
  bottomRow: { display: 'grid', gridTemplateColumns: '2fr 3fr', gap: 16 },
  card: {
    background: '#fff', borderRadius: 16, padding: '20px 22px',
    border: '1px solid #e8edf3', boxShadow: '0 1px 8px rgba(0,0,0,0.05)'
  },
  cardTitle: { fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', margin: 0 },
  cardSub: { fontSize: '0.78rem', color: '#94a3b8', marginTop: 2, marginBottom: 16 },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14 },
  chip: (bg, color) => ({
    background: bg, color: color, fontSize: '0.68rem', fontWeight: 600,
    padding: '3px 10px', borderRadius: 99
  }),
  summaryGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  summaryTile: (bg) => ({
    background: bg, borderRadius: 12, padding: '14px 16px'
  }),
  summaryLabel: (color) => ({ fontSize: '0.75rem', fontWeight: 600, color, textTransform: 'uppercase', letterSpacing: '0.4px' }),
  summaryValue: (color) => ({ fontSize: '1.5rem', fontWeight: 800, color, marginTop: 4 }),
};

/* ─── Bar Chart ─────────────────────────────────────────────────────────────── */
function BarChart({ data = [], color = '#6366f1', height = 90 }) {
  if (!data.length) return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>
      Tidak ada data
    </div>
  );
  const max = Math.max(...data.map(d => Number(d.value)), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height, padding: '6px 0 0' }}>
        {data.map((d, i) => {
          const h = Math.max(8, (Number(d.value) / max) * (height - 8));
          const alpha = 0.55 + (i / Math.max(data.length, 1)) * 0.45;
          return (
            <div key={i} title={`${d.label}: ${d.value}`}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-end', height: '100%' }}>
              <div style={{
                height: h,
                background: `linear-gradient(180deg, ${color} 0%, ${color}cc 100%)`,
                opacity: alpha,
                borderRadius: '10px 10px 4px 4px',
                width: '100%',
                minWidth: 24,
                boxShadow: '0 8px 18px rgba(245, 158, 11, 0.18)',
                transition: 'height 0.6s ease'
              }} />
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: '0.72rem', fontWeight: 600, color: '#64748b' }}>
            {d.label}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Line Chart ────────────────────────────────────────────────────────────── */
function LineChart({ data = [], color = '#10b981', height = 90 }) {
  if (data.length < 2) return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>
      Tidak ada data
    </div>
  );
  const max = Math.max(...data.map(d => Number(d.value)), 1);
  const w = 400; const pad = 8;
  const coords = data.map((d, i) => ({
    x: pad + (i / (data.length - 1)) * (w - pad * 2),
    y: pad + ((1 - Number(d.value) / max) * (height - pad * 2))
  }));
  const polyline = coords.map(p => `${p.x},${p.y}`).join(' ');
  const fill = `${pad},${height} ${polyline} ${w - pad},${height}`;
  const gradId = `lg${color.replace('#', '')}`;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={fill} fill={`url(#${gradId})`} />
      <polyline points={polyline} fill="none" stroke={color} strokeWidth="2.5"
        strokeLinejoin="round" strokeLinecap="round" />
      {coords.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="4" fill="#fff" stroke={color} strokeWidth="2" />
      ))}
    </svg>
  );
}

/* ─── Horizontal Bars ───────────────────────────────────────────────────────── */
function HBar({ data = [], color = '#6366f1' }) {
  const max = Math.max(...data.map(d => d.sessions_count), 1);
  const palette = ['#6366f1', '#818cf8', '#a5b4fc', '#c7d2fe', '#e0e7ff'];

  const getInitials = (name = '') =>
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || '?';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 }}>
      {data.map((d, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, maxWidth: '68%', minWidth: 0 }}>
              {d.photo_url ? (
                <img
                  src={d.photo_url}
                  alt={d.name}
                  style={{ width: 28, height: 28, borderRadius: '999px', objectFit: 'cover', flexShrink: 0, border: '1px solid #e2e8f0' }}
                />
              ) : (
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '999px',
                    background: palette[i] || color,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    flexShrink: 0
                  }}
                >
                  {getInitials(d.name)}
                </div>
              )}
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.name}
              </span>
            </div>
            <span style={{ fontSize: '0.78rem', color: '#64748b' }}>{d.sessions_count} sesi</span>
          </div>
          <div style={{ background: '#f1f5f9', borderRadius: 99, height: 8 }}>
            <div style={{
              width: `${(d.sessions_count / max) * 100}%`,
              background: palette[i] || color,
              height: 8, borderRadius: 99, transition: 'width 0.8s ease'
            }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Stat Card ─────────────────────────────────────────────────────────────── */
function StatCard({ icon: Icon, label, value, sub, gradient }) {
  return (
    <div style={S.kpiCard(gradient)}>
      <div>
        <div style={S.kpiLabel}>{label}</div>
        <div style={S.kpiValue}>{value}</div>
        {sub && <div style={S.kpiSub}>{sub}</div>}
      </div>
      <div style={S.kpiIcon}><Icon size={20} color="white" /></div>
    </div>
  );
}

/* ─── Admin Dashboard ───────────────────────────────────────────────────────── */
function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshed, setRefreshed] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/dashboard/stats`,
        { withCredentials: true }
      );
      setStats(res.data.stats);
      setCharts(res.data.charts);
      setRefreshed(new Date().toLocaleTimeString('id-ID'));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const fmt = n => Number(n || 0).toLocaleString('id-ID');
  const fmtRp = n => 'Rp\u00a0' + Number(n || 0).toLocaleString('id-ID');
  const incomeChart = (charts?.income_by_month || []).map((item) => ({
    label: item.label,
    value: Number(item.value) || 0,
  }));
  const sessionChart = (charts?.sessions_by_month || []).map((item) => ({
    label: item.label,
    value: Number(item.value) || 0,
  }));

  if (loading && !stats) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 360, flexDirection: 'column', gap: 14 }}>
        <div className="spinner-border text-primary" />
        <span style={{ color: '#64748b', fontSize: '0.85rem' }}>Memuat data statistik…</span>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Dashboard Overview</h1>
          <p style={S.subtitle}>Ringkasan statistik sistem secara realtime</p>
        </div>
        <button style={S.refreshBtn} onClick={fetchStats} disabled={loading}>
          <RefreshCw size={14} />
          {loading ? 'Memuat...' : 'Refresh'}
        </button>
      </div>

      {/* KPI Cards — 4 kolom */}
      <div style={S.kpiGrid}>
        <StatCard icon={Users} label="Total Pengguna"
          value={fmt(stats?.total_users)} sub="Terdaftar di sistem"
          gradient="linear-gradient(135deg,#6366f1 0%,#818cf8 100%)" />
        <StatCard icon={FileText} label="Total Dokumen"
          value={fmt(stats?.total_documents)} sub="OCR + klasik"
          gradient="linear-gradient(135deg,#0ea5e9 0%,#38bdf8 100%)" />
        <StatCard icon={MessageSquare} label="Sesi Chat"
          value={fmt(stats?.total_sessions)} sub={`Avg ${stats?.avg_chats_per_user}/user`}
          gradient="linear-gradient(135deg,#10b981 0%,#34d399 100%)" />
        <StatCard icon={TrendingUp} label="Total Income"
          value={fmtRp(stats?.total_income)} sub="Transaksi SETTLEMENT"
          gradient="linear-gradient(135deg,#f59e0b 0%,#fbbf24 100%)" />
      </div>

      {/* Charts — 2 kolom */}
      <div style={S.chartsRow}>
        {/* Income */}
        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <div style={{ background: '#fef3c7', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp size={15} color="#f59e0b" />
            </div>
            <p style={S.cardTitle}>Pendapatan per Bulan</p>
            <ArrowUpRight size={14} style={{ color: '#10b981', marginLeft: 'auto' }} />
          </div>
          <p style={S.cardSub}>Akumulasi transaksi SETTLEMENT 3 bulan terakhir</p>
          <BarChart data={incomeChart} color="#f59e0b" height={110} />
          <div style={S.chipRow}>
            {incomeChart.map((d, i) => (
              <span key={i} style={S.chip('#fef3c7', '#92400e')}>{d.label}: {fmtRp(d.value)}</span>
            ))}
          </div>
        </div>

        {/* Sessions */}
        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <div style={{ background: '#d1fae5', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Activity size={15} color="#10b981" />
            </div>
            <p style={S.cardTitle}>Sesi Chat per Bulan</p>
            <ArrowUpRight size={14} style={{ color: '#10b981', marginLeft: 'auto' }} />
          </div>
          <p style={S.cardSub}>Pertumbuhan aktivitas pengguna 3 bulan terakhir</p>
          <LineChart data={sessionChart} color="#10b981" height={90} />
          <div style={S.chipRow}>
            {sessionChart.map((d, i) => (
              <span key={i} style={S.chip('#d1fae5', '#065f46')}>{d.label}: {fmt(d.value)} sesi</span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom — Top Users + Summary */}
      <div style={S.bottomRow}>
        {/* Top Users */}
        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <div style={{ background: '#ede9fe', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={15} color="#6366f1" />
            </div>
            <p style={S.cardTitle}>Top 5 Pengguna Aktif</p>
          </div>
          <p style={S.cardSub}>Berdasarkan jumlah sesi chat</p>
          {(charts?.top_users || []).length === 0
            ? <div style={{ color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center', padding: '20px 0' }}>Belum ada data</div>
            : <HBar data={charts?.top_users || []} />
          }
        </div>

        {/* Summary */}
        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <div style={{ background: '#e0f2fe', borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BarChart2 size={15} color="#0ea5e9" />
            </div>
            <p style={S.cardTitle}>Ringkasan Sistem</p>
          </div>
          <p style={S.cardSub}>Angka keseluruhan data dalam sistem</p>
          <div style={S.summaryGrid}>
            {[
              { label: 'Total Pesan', value: fmt(stats?.total_messages), color: '#0369a1', bg: '#e0f2fe' },
              { label: 'Avg Chat/User', value: `${stats?.avg_chats_per_user} sesi`, color: '#4338ca', bg: '#ede9fe' },
              { label: 'Total Pengguna', value: fmt(stats?.total_users), color: '#065f46', bg: '#d1fae5' },
              { label: 'Total Dokumen', value: fmt(stats?.total_documents), color: '#92400e', bg: '#fef3c7' },
            ].map((item, i) => (
              <div key={i} style={S.summaryTile(item.bg)}>
                <div style={S.summaryLabel(item.color)}>{item.label}</div>
                <div style={S.summaryValue(item.color)}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {refreshed && (
        <div style={{ textAlign: 'right', marginTop: 14 }}>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Terakhir diperbarui: {refreshed}</span>
        </div>
      )}

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 1024px) {
          .dash-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .dash-charts-row { grid-template-columns: 1fr !important; }
          .dash-bottom-row { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 640px) {
          .dash-kpi-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </div>
  );
}

/* ─── User Welcome ──────────────────────────────────────────────────────────── */
function UserWelcome({ user }) {
  const { appSetting } = useAppSetting();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', textAlign: 'center' }}>
      <img src={appSetting?.logo_image ?? icon} alt="Logo" style={{ width: 190, height: 'auto', marginBottom: 24 }} />
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>Selamat Datang, {user?.name}</h2>
      <p style={{ fontSize: '0.88rem', color: '#64748b', marginTop: 8 }}>Silakan pilih menu dari sidebar untuk mulai menggunakan aplikasi</p>
    </div>
  );
}

/* ─── Export ────────────────────────────────────────────────────────────────── */
export default function DashboardPage({ user }) {
  if (user?.role === 'ADMIN') return <AdminDashboard />;
  return <UserWelcome user={user} />;
}
